import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import { Facebook, Instagram, Phone, Mail, MapPin } from 'lucide-react';
import { toast } from 'sonner';

// Payment method logos and compliance badges
const PAYMENT_LOGOS_URL = 'https://customer-assets.emergentagent.com/job_sheet-commerce-1/artifacts/6x6ydea7_payments.webp';
const ANPC_SOL_URL = 'https://customer-assets.emergentagent.com/job_sheet-commerce-1/artifacts/0jokj3py_sol.webp';
const ANPC_SAL_URL = 'https://customer-assets.emergentagent.com/job_sheet-commerce-1/artifacts/xxtfpyj5_anpc.webp';
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_841fbfa7-1a20-4a56-bfc0-aa4a083c4873/artifacts/mbzov694_Logo-Rani_final.webp';

export const Footer = () => {
  const { t, settings, API, language } = useApp();
  const [email, setEmail] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const newsletterText = {
    ro: {
      title: 'Intră în comunitatea RaniStore.',
      subtitle: 'Nu rata noutățile - abonează-te acum și fii mereu cu un pas înainte!',
      placeholder: 'Adresa ta de email',
      button: 'Abonează-te',
      success: 'Te-ai abonat cu succes!',
      agree: 'Sunt de acord cu primirea ofertelor'
    },
    en: {
      title: 'Join the RaniStore community.',
      subtitle: "Don't miss the news - subscribe now and always stay one step ahead!",
      placeholder: 'Your email address',
      button: 'Subscribe',
      success: 'Successfully subscribed!',
      agree: 'I agree to receive offers'
    }
  };

  const txt = newsletterText[language] || newsletterText.ro;

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email || !agreed) return;
    setSubmitting(true);
    try {
      await fetch(`${API}/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      toast.success(txt.success);
      setEmail('');
      setAgreed(false);
    } catch (err) {
      toast.success(txt.success);
    } finally {
      setSubmitting(false);
    }
  };

  // Information column pages (remove ANPC-SAL from here since it's in RaniStore column now)
  const informationPages = [
    { label: language === 'ro' ? 'Contact' : 'Contact', path: '/contact' },
    { label: language === 'ro' ? 'Metode de plată' : 'Payment Methods', path: '/page/payment-methods' },
    { label: language === 'ro' ? 'Livrare și expediere' : 'Shipping and Delivery', path: '/page/shipping-delivery' },
    { label: language === 'ro' ? 'Politica de retur' : 'Return Policy', path: '/page/return-policy' },
    { label: language === 'ro' ? 'Formular retur' : 'Return Form', path: '/formular-de-retur' },
  ];

  // RaniStore column pages
  const raniStorePages = [
    { label: language === 'ro' ? 'Despre noi' : 'About Us', path: '/page/despre-noi' },
    { label: language === 'ro' ? 'Termeni și Condiții' : 'Terms and Conditions', path: '/page/termeni-si-conditii' },
    { label: language === 'ro' ? 'Politica Cookies' : 'Cookies Policy', path: '/page/cookies-policy' },
    { label: language === 'ro' ? 'Prelucrarea datelor personale' : 'Processing of personal data', path: '/page/personal-data' },
    { label: 'ANPC', path: 'https://anpc.ro/', external: true },
    { label: 'ANPC - SAL', path: 'https://reclamatiisal.anpc.ro/', external: true },
  ];

  return (
    <footer className="bg-[#0D0B14] text-white">
      {/* Newsletter Section */}
      <div className="border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="max-w-lg mx-auto text-center">
            <h3 className="text-xl font-heading font-semibold text-white mb-2">
              {txt.title}
            </h3>
            <p className="text-gray-400 text-sm mb-4">
              {txt.subtitle}
            </p>
            <form onSubmit={handleSubscribe} className="space-y-3">
              <div className="flex gap-2">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={txt.placeholder}
                  className="flex-1 px-4 py-2.5 rounded-lg bg-gray-800 border border-gray-700 text-white placeholder-gray-500 focus:outline-none focus:border-gold"
                  required
                />
                <button
                  type="submit"
                  disabled={submitting || !agreed}
                  className="px-6 py-2.5 bg-gold hover:bg-yellow-500 text-[#1A1625] font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? '...' : txt.button}
                </button>
              </div>
              <label className="flex items-center justify-center gap-2 cursor-pointer text-sm text-gray-400">
                <input
                  type="checkbox"
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-600 bg-gray-800 text-gold focus:ring-gold"
                />
                <span>{txt.agree}</span>
              </label>
            </form>
          </div>
        </div>
      </div>

      {/* Main Footer Content - 4 Columns */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          {/* Column 1: Informații */}
          <div>
            <h4 className="font-heading text-lg text-gold mb-6">{language === 'ro' ? 'Informații' : 'Information'}</h4>
            <ul className="space-y-3">
              {informationPages.map((page, idx) => (
                <li key={idx}>
                  {page.external ? (
                    <a
                      href={page.path}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-400 hover:text-gold transition-colors text-sm"
                    >
                      {page.label}
                    </a>
                  ) : (
                    <Link
                      to={page.path}
                      className="text-gray-400 hover:text-gold transition-colors text-sm"
                    >
                      {page.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Column 2: Rani Store */}
          <div>
            <h4 className="font-heading text-lg text-gold mb-6">Rani Store</h4>
            <ul className="space-y-3">
              {raniStorePages.map((page, idx) => (
                <li key={idx}>
                  {page.external ? (
                    <a
                      href={page.path}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-400 hover:text-gold transition-colors text-sm"
                    >
                      {page.label}
                    </a>
                  ) : (
                    <Link
                      to={page.path}
                      className="text-gray-400 hover:text-gold transition-colors text-sm"
                    >
                      {page.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3: Contact */}
          <div>
            <h4 className="font-heading text-lg text-gold mb-6">Contact</h4>
            <ul className="space-y-4">
              <li className="flex items-center gap-3 text-gray-400">
                <MapPin className="h-4 w-4 text-gold flex-shrink-0" />
                <span className="text-sm">București, România</span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="h-4 w-4 text-gold flex-shrink-0" />
                <a href="tel:0732901128" className="text-gray-400 hover:text-gold transition-colors text-sm">
                  0732901128
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="h-4 w-4 text-gold flex-shrink-0" />
                <a href="mailto:office@ranistore.ro" className="text-gray-400 hover:text-gold transition-colors text-sm">
                  office@ranistore.ro
                </a>
              </li>
            </ul>
            
            {/* Social Icons with Brand Colors */}
            <div className="flex gap-3 mt-6">
              {settings?.social_links?.facebook && (
                <a
                  href={settings.social_links.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-[#1877F2] rounded-full flex items-center justify-center text-white hover:brightness-110 transition-all"
                  aria-label="Facebook"
                >
                  <Facebook className="h-5 w-5" />
                </a>
              )}
              {settings?.social_links?.instagram && (
                <a
                  href={settings.social_links.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full flex items-center justify-center text-white hover:brightness-110 transition-all"
                  style={{ background: 'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)' }}
                  aria-label="Instagram"
                >
                  <Instagram className="h-5 w-5" />
                </a>
              )}
              {settings?.social_links?.tiktok && (
                <a
                  href={settings.social_links.tiktok}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-black rounded-full flex items-center justify-center text-white hover:brightness-110 transition-all"
                  aria-label="TikTok"
                >
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                  </svg>
                </a>
              )}
              {settings?.social_links?.pinterest && (
                <a
                  href={settings.social_links.pinterest}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-[#E60023] rounded-full flex items-center justify-center text-white hover:brightness-110 transition-all"
                  aria-label="Pinterest"
                >
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 0a12 12 0 0 0-4.37 23.17c-.1-.94-.2-2.4.04-3.44l1.4-5.94s-.36-.72-.36-1.78c0-1.66.96-2.9 2.16-2.9 1.02 0 1.52.77 1.52 1.68 0 1.02-.66 2.56-1 3.98-.28 1.2.6 2.18 1.78 2.18 2.14 0 3.78-2.26 3.78-5.5 0-2.88-2.06-4.88-5.02-4.88-3.42 0-5.42 2.56-5.42 5.22 0 1.04.4 2.14.9 2.74.1.12.1.22.08.34l-.34 1.36c-.06.22-.18.26-.42.16-1.56-.72-2.54-3-2.54-4.84 0-3.94 2.86-7.56 8.26-7.56 4.34 0 7.7 3.1 7.7 7.22 0 4.32-2.72 7.78-6.5 7.78-1.26 0-2.46-.66-2.86-1.44l-.78 2.96c-.28 1.1-1.04 2.48-1.56 3.32A12 12 0 1 0 12 0z"/>
                  </svg>
                </a>
              )}
            </div>
          </div>

          {/* Column 4: Company Info */}
          <div>
            <h4 className="font-heading text-lg text-gold mb-6">{language === 'ro' ? 'Date Firmă' : 'Company Info'}</h4>
            <div className="space-y-2 text-sm text-gray-400">
              <p className="font-medium text-white">S.C. RANISTORE S.R.L.</p>
              <p>Str. Spiești, Nr. 51, Sector 3, București</p>
              <p>J40 / 2443 / 2020</p>
              <p>CUI 42291550</p>
              <p>
                <a href="tel:0732901128" className="hover:text-gold transition-colors">0732901128</a>
              </p>
              <p>
                <a href="mailto:office@ranistore.ro" className="hover:text-gold transition-colors">office@ranistore.ro</a>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Payment & Compliance Section */}
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            {/* Payment Methods - smaller */}
            <div className="flex items-center gap-4">
              <img 
                src={PAYMENT_LOGOS_URL} 
                alt="Visa, Mastercard, PayPal, Apple Pay" 
                className="h-5 object-contain opacity-80"
              />
            </div>

            {/* ANPC Badges */}
            <div className="flex items-center gap-3">
              <a 
                href="https://anpc.ro/ce-categorie/solutionarea-online-a-litigiilor/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="hover:opacity-80 transition-opacity"
              >
                <img 
                  src={ANPC_SOL_URL} 
                  alt="SOL" 
                  className="h-8 object-contain rounded"
                />
              </a>
              <a 
                href="https://anpc.ro/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="hover:opacity-80 transition-opacity"
              >
                <img 
                  src={ANPC_SAL_URL} 
                  alt="ANPC" 
                  className="h-8 object-contain rounded"
                />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Copyright */}
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <p className="text-center text-gray-500 text-sm">
            © 2026 Rani Store. Toate drepturile rezervate.
          </p>
        </div>
      </div>
    </footer>
  );
};
